import { useCallback, useEffect, useState } from 'preact/hooks';

// The native `storage` event only fires in *other* tabs, so writes made in this
// tab (via the hook's setter or writeSessionStorage) would not notify other hook
// instances of the same key. This same-tab event closes that gap.
const SESSION_STORAGE_EVENT = 'session-storage:in-tab';

/**
 * Write a session-storage key and notify same-tab `useSessionStorage(key)` subscribers.
 * Use this instead of `sessionStorage.setItem` when a value read by the hook is written
 * from outside a component (e.g. migrations), so subscribers re-render.
 */
export const writeSessionStorage = <T>(key: string, value: T) => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent(SESSION_STORAGE_EVENT, { detail: { key } }));
};

const useSessionStorage = <T>(key: string, initialValue: T) => {
  const [value, setValue] = useState(() => {
    if (typeof sessionStorage !== 'undefined') {
      const strValue = sessionStorage.getItem(key);
      if (!strValue) {
        return initialValue;
      }
      try {
        return JSON.parse(strValue) as T;
      } catch {
        return initialValue;
      }
    }
    return initialValue;
  });

  const setter = useCallback(
    (newValue: T) => {
      setValue(newValue);
      writeSessionStorage(key, newValue);
    },
    [key],
  );

  useEffect(() => {
    const syncFromStorage = () => {
      const strValue = sessionStorage.getItem(key);
      if (strValue == null) {
        setValue(initialValue);
        return;
      }
      try {
        setValue(JSON.parse(strValue) as T);
      } catch {
        setValue(initialValue);
      }
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.storageArea === sessionStorage && event.oldValue !== event.newValue) {
        setValue(event.newValue == null ? initialValue : JSON.parse(event.newValue));
      }
    };

    const handleInTabChange = (event: Event) => {
      if ((event as CustomEvent<{ key: string }>).detail?.key === key) {
        syncFromStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(SESSION_STORAGE_EVENT, handleInTabChange);
    // Catch writes that landed before this subscriber attached (e.g. legacy migration).
    syncFromStorage();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(SESSION_STORAGE_EVENT, handleInTabChange);
    };
  }, [key, initialValue]);

  return [value, setter] as const;
};

export default useSessionStorage;
